// src/Dashboard.js - Imports section
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  Divider, 
  CircularProgress, 
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Title, useDataProvider, useRedirect } from 'react-admin';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import EventIcon from '@mui/icons-material/Event';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import PeopleIcon from '@mui/icons-material/People';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useOnDutyContact } from './hooks/useOnDutyContact';
import { useCalendarStatus } from './hooks/useCalendarStatus';
import { useCalendarContactCheck } from './hooks/useCalendarContactCheck';
import { useQuery } from 'react-query';
import { extractTenantFromHostname } from './utils/tenant';

// Calendar Status Indicator Component
const CalendarStatusIndicator = ({ calendar }) => {
  const teamId = localStorage.getItem('userTeamId');
  const { 
    data: statusData, 
    isLoading,
    isFetching
  } = useCalendarStatus(teamId, calendar.id, {
    // For dashboard, we can use a longer staleTime to avoid too many requests
    staleTime: 45 * 60 * 1000, // 45 minutes
  });
  
  if (isLoading || isFetching) {
    return <CircularProgress size={16} />;
  }
  
  const isSuccess = statusData?.status === 'success';
  
  return (
    <Tooltip title={statusData?.message || (isSuccess ? 'Available' : 'Error')}>
      {isSuccess ? 
        <CheckCircleIcon fontSize="small" color="success" /> : 
        <ErrorIcon fontSize="small" color="error" />
      }
    </Tooltip>
  );
};

// Calendar Stats Component for Dashboard
const CalendarStats = () => {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const teamId = localStorage.getItem('userTeamId');
  const dataProvider = useDataProvider();
  
  useEffect(() => {
    const fetchCalendars = async () => {
      if (!teamId) return;
      
      try {
        setLoading(true);
        const { data } = await dataProvider.getList('calendars', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'fullname', order: 'ASC' }
        });
        setCalendars(data);
      } catch (error) {
        console.error('Error fetching calendars:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCalendars();
  }, [teamId, dataProvider]);
  
  if (loading) {
    return <CircularProgress size={24} />;
  }
  
  if (calendars.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No calendars configured
      </Typography>
    );
  }
  
  return (
    <Box>
      {calendars.map(calendar => (
        <Box 
          key={calendar.id} 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
            {calendar.fullname}
          </Typography>
          <CalendarStatusIndicator calendar={calendar} />
        </Box>
      ))}
    </Box>
  );
};

// Calendar Warning Widget - shows when there are unmatched keywords in calendars
const CalendarWarningWidget = () => {
  const teamId = localStorage.getItem('userTeamId');
  const {
    data: contactCheckData,
    isLoading,
    error
  } = useCalendarContactCheck(teamId, {
    staleTime: 45 * 60 * 1000, // 45 minutes
  });

  // Don't show widget if loading, error, or no data
  if (isLoading || error || !contactCheckData) {
    return null;
  }

  // Find calendars with WARNING status
  const warningCalendars = contactCheckData.calendars?.filter(
    calendar => calendar.status === 'WARNING'
  ) || [];

  // Don't show widget if no warnings
  if (warningCalendars.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
        <Typography variant="h5">Calendar Contact Warnings</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          Some calendars contain keywords that don't match any contacts:
        </Typography>

        {warningCalendars.map((calendar, index) => (
          <Box key={index} sx={{ mb: calendar.unmatchedKeywords?.length > 0 ? 2 : 1 }}>
            <Typography variant="h6" sx={{ color: 'warning.dark', mb: 1 }}>
              {calendar.calendarName}
            </Typography>

            {calendar.unmatchedKeywords && calendar.unmatchedKeywords.length > 0 && (
              <Box sx={{ ml: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Unmatched keywords:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {calendar.unmatchedKeywords.map((keyword, keywordIndex) => (
                    <Typography
                      key={keywordIndex}
                      variant="body2"
                      sx={{
                        backgroundColor: 'warning.main',
                        color: 'warning.contrastText',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontWeight: 500
                      }}
                    >
                      {keyword}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        ))}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 'bold' }}>
          Update the contacts or your calendar to resolve this warning.
        </Typography>
      </Paper>
    </Box>
  );
};

// Recent Calls Component for Dashboard
const RecentCalls = () => {
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const teamId = localStorage.getItem('userTeamId');

  // Use React Query to fetch the voipNumber setting
  const {
    data: voipNumber,
    isLoading: voipLoading,
    error: voipError
  } = useQuery(
    ['voipNumber', teamId],
    async () => {
      if (!teamId) {
        throw new Error('Team ID not available');
      }

      // Get settings to find the voipnumber
      const { data: settings } = await dataProvider.getList('settings', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'key', order: 'ASC' }
      });

      // Find the voipnumber setting
      const voipSetting = settings.find(setting => setting.key === 'voipnumber');
      if (voipSetting) {
        return voipSetting.value;
      } else {
        throw new Error('VOIP number setting not found');
      }
    },
    {
      enabled: !!teamId,
      staleTime: 60000, // 1 minute cache
      retry: 1
    }
  );

  // Use React Query to fetch the VOIP last updated timestamp
  const {
    data: voipStatus,
    isLoading: statusLoading,
    error: statusError
  } = useQuery(
    ['voipStatus', teamId],
    async () => {
      // Get the tenant from hostname
      const tenant = extractTenantFromHostname();

      // Determine the API URL based on environment (read from runtime config)
      const nodeEnv = window.APP_CONFIG?.NODE_ENV || 'production';

      let apiUrl;
      if (nodeEnv === 'development') {
        apiUrl = `http://${tenant}.api.wygc.eu:8080/api/voip/lastupdated`;
      } else {
        apiUrl = `https://${tenant}.api.wygc.eu/api/voip/lastupdated`;
      }

      console.log(`Fetching VOIP last updated from: ${apiUrl}`);

      // Get authorization token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not available');
      }

      // Use fetch directly since we're calling an external API
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      return response.json();
    },
    {
      enabled: !!teamId,
      staleTime: 60000, // 1 minute cache
      retry: 1
    }
  );
  
  // Use React Query to fetch the call history once we have the voipNumber
  const { 
    data: calls, 
    isLoading: callsLoading, 
    error: callsError, 
    refetch: refetchCalls 
  } = useQuery(
    ['recentCalls', teamId, voipNumber],
    async () => {
      if (!voipNumber) {
        throw new Error('VOIP number not available');
      }
      
      // Get the tenant from hostname
      const tenant = extractTenantFromHostname();
      
      // Determine the API URL based on environment (read from runtime config)
      const nodeEnv = window.APP_CONFIG?.NODE_ENV || 'production';

      let apiUrl;
      if (nodeEnv === 'development') {
        apiUrl = `http://${tenant}.api.wygc.eu:8080/api/call/matched?team_id=${teamId}&calleeId=${voipNumber}&limit=5`;
      } else {
        apiUrl = `https://${tenant}.api.wygc.eu/api/call/matched?team_id=${teamId}&calleeId=${voipNumber}&limit=5`;
      }
      
      console.log(`Fetching recent calls from: ${apiUrl} for team ${teamId}`);
      
      // Get authorization token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Use fetch directly since we're calling an external API
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      return response.json();
    },
    {
      enabled: !!teamId && !!voipNumber, // Only run when we have both teamId and voipNumber
      staleTime: 60000, // 1 minute cache
      retry: 1
    }
  );
  
  // Format date and time for display
  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';

    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return isoString;
    }
  };

  // Format last updated timestamp for Europe/Amsterdam timezone
  const formatLastUpdated = (isoString) => {
    if (!isoString) return '';

    try {
      const date = new Date(isoString);
      return date.toLocaleString('nl-NL', {
        timeZone: 'Europe/Amsterdam',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  // Format duration in seconds to mm:ss format
  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle manual refresh
  const handleRefresh = () => {
    refetchCalls();
  };

  // Determine overall loading state
  const isLoading = voipLoading || callsLoading || statusLoading;
  
  // Combine error messages
  const errorMessage = voipError?.message || callsError?.message;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            }
          }}
          onClick={() => redirect('/callHistory')}
        >
          <PhoneCallbackIcon sx={{ mr: 1, color: 'secondary.main' }} />
          <Typography variant="h5">
            Recent Call History
          </Typography>
          {voipStatus?.lastUpdated && (
            <Typography
              variant="caption"
              sx={{ ml: 1, color: 'text.secondary', fontSize: '0.75rem' }}
            >
              last updated: {formatLastUpdated(voipStatus.lastUpdated)}
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh call history">
          <IconButton
            onClick={handleRefresh}
            disabled={isLoading}
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />
      
      <Paper elevation={3}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : errorMessage ? (
          <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
            <Typography variant="body1">{errorMessage}</Typography>
          </Box>
        ) : !calls || calls.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">No recent calls found</Typography>
          </Box>
        ) : (
          // Mobile-first responsive design for call history
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {/* Mobile Card Layout */}
            {calls.map((call, index) => (
              <Paper 
                key={index} 
                elevation={1} 
                sx={{ 
                  mb: 2, 
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Caller ID</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {call.callerId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Date/Time</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(call.inboundCall.startedOn)}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Forwarded to</Typography>
                    {(() => {
                      const forwardedText = call.forwardResult.forwarded
                        ? (call.outboundCall.calleeId || (call.forwardResult.reason === 'no_schedule' ? 'No schedule' : 'N/A'))
                        : 'Not forwarded';

                      if (forwardedText === 'No schedule') {
                        return (
                          <Tooltip title="No appointments (events) were found in the calendar(s) when this call was processed." arrow>
                            <Box sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              cursor: 'help',
                              color: 'warning.main'
                            }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {forwardedText}
                              </Typography>
                              <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 14 }} />
                            </Box>
                          </Tooltip>
                        );
                      }
                      if (forwardedText === 'Not forwarded') {
                        return (
                          <Tooltip title="Due to a technical issue the call could not be forwarded. The WhoYouGonnaCall team is already notified of this event." arrow>
                            <Box sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              cursor: 'help',
                              color: 'error.main'
                            }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {forwardedText}
                              </Typography>
                              <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 14 }} />
                            </Box>
                          </Tooltip>
                        );
                      }
                      return (
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {forwardedText}
                        </Typography>
                      );
                    })()}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {call.outboundCall?.fullname || '-'}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start'
                    }}>
                      <Typography variant="subtitle2" color="text.secondary">Call Answered</Typography>
                      <Box sx={{
                        color: call.forwardResult.answered ? 'green' : 'red',
                        fontWeight: 'bold',
                        fontSize: '1.2em',
                        mt: 0.5
                      }}>
                        {(() => {
                          // Check if there's a phone number in forwardedTo
                          const forwardedText = call.forwardResult.forwarded
                            ? (call.outboundCall.calleeId || (call.forwardResult.reason === 'no_schedule' ? 'No schedule' : 'N/A'))
                            : 'Not forwarded';

                          const hasPhoneNumber = forwardedText &&
                            forwardedText !== 'No schedule' &&
                            forwardedText !== 'Not forwarded' &&
                            forwardedText !== '-' &&
                            forwardedText !== 'N/A';

                          return hasPhoneNumber ? (call.forwardResult.answered ? '✓' : '✗') : '-';
                        })()}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                    <Typography variant="body2">
                      {call.forwardResult.forwarded 
                        ? formatDuration(call.outboundCall.durationInSeconds)
                        : formatDuration(call.inboundCall.durationInSeconds)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        )}
        
        {!isLoading && !errorMessage && calls && calls.length > 0 && (
          // Desktop Table Layout
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Date/Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Caller ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Forwarded to</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Full Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">Call Answered</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calls.map((call, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ minWidth: 140 }}>{formatDateTime(call.inboundCall.startedOn)}</TableCell>
                      <TableCell sx={{ minWidth: 120, wordBreak: 'break-all' }}>{call.callerId}</TableCell>
                      <TableCell sx={{ minWidth: 140, wordBreak: 'break-all' }}>
                        {(() => {
                          const forwardedText = call.forwardResult.forwarded
                            ? (call.outboundCall.calleeId || (call.forwardResult.reason === 'no_schedule' ? 'No schedule' : 'N/A'))
                            : 'Not forwarded';

                          if (forwardedText === 'No schedule') {
                            return (
                              <Tooltip title="No appointments (events) were found in the calendar(s) when this call was processed." arrow>
                                <Box sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: 'help',
                                  color: 'warning.main',
                                  '&:hover': {
                                    color: 'warning.dark',
                                  }
                                }}>
                                  <span>{forwardedText}</span>
                                  <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 16 }} />
                                </Box>
                              </Tooltip>
                            );
                          }
                          if (forwardedText === 'Not forwarded') {
                            return (
                              <Tooltip title="Due to a technical issue the call could not be forwarded. The WhoYouGonnaCall team is already notified of this event." arrow>
                                <Box sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: 'help',
                                  color: 'error.main',
                                  '&:hover': {
                                    color: 'error.dark',
                                  }
                                }}>
                                  <span>{forwardedText}</span>
                                  <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 16 }} />
                                </Box>
                              </Tooltip>
                            );
                          }
                          return forwardedText;
                        })()}
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>{call.outboundCall?.fullname || '-'}</TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        {(() => {
                          // Check if there's a phone number in forwardedTo
                          const forwardedText = call.forwardResult.forwarded
                            ? (call.outboundCall.calleeId || (call.forwardResult.reason === 'no_schedule' ? 'No schedule' : 'N/A'))
                            : 'Not forwarded';

                          const hasPhoneNumber = forwardedText &&
                            forwardedText !== 'No schedule' &&
                            forwardedText !== 'Not forwarded' &&
                            forwardedText !== '-' &&
                            forwardedText !== 'N/A';

                          if (!hasPhoneNumber) {
                            return '-';
                          }

                          return (
                            <Box sx={{
                              color: call.forwardResult.answered ? 'green' : 'red',
                              fontWeight: 'bold',
                              fontSize: '1.2em',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}>
                              {call.forwardResult.answered ? '✓' : '✗'}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ minWidth: 80 }}>
                        {call.forwardResult.forwarded 
                          ? formatDuration(call.outboundCall.durationInSeconds)
                          : formatDuration(call.inboundCall.durationInSeconds)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const Dashboard = () => {
  // State holds the last successfully loaded data as a backup
  const [counts, setCounts] = useState({
    contacts: 0,
    calendars: 0,
    users: 0,
    teams: 0
  });
  // State for maximum limits
  const [limits, setLimits] = useState({
    max_contacts: null,
    max_calendars: null
  });
  const [voipNumber, setVoipNumber] = useState(null);
  const [isRefreshingOnDuty, setIsRefreshingOnDuty] = useState(false);
  const dataProvider = useDataProvider();
  const teamId = localStorage.getItem('userTeamId');
  const role = localStorage.getItem('userRole');

  // Format phone number with mask +99 99 999 9999
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Apply the mask +99 99 999 9999
    if (cleaned.length >= 11) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 11)}`;
    }
    
    // Return with + if number is too short
    return '+' + cleaned;
  };

  // Use the dedicated hook for on-duty contact with proper refetch behavior
  const { 
    data: onDutyContact, 
    isLoading: onDutyLoading, 
    error: onDutyError,
    refetch: refetchOnDuty,
    isFetching: isOnDutyFetching
  } = useOnDutyContact(teamId, {
    // Don't refetch on mount or when the window regains focus
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  // Fetch resource counts - will run on every component mount
  const { isLoading: countsLoading, refetch: refetchCounts } = useQuery(
    ['dashboardCounts', teamId],
    async () => {
      try {
        // Get counts for resources available to all roles
        const contactsResult = await dataProvider.getList('contacts', {
          pagination: { page: 1, perPage: 1000 }
        });
        const calendarsResult = await dataProvider.getList('calendars', {
          pagination: { page: 1, perPage: 100 }
        });
        
        let updatedCounts = {
          contacts: contactsResult.data.length,
          calendars: calendarsResult.data.length,
          users: 0,
          teams: 0 // This will be updated separately
        };
        
        // If tenant_admin, also fetch users count
        if (role === 'tenant_admin' && teamId) {
          try {
            const usersResult = await dataProvider.getList('users', {
              pagination: { page: 1, perPage: 100 }
            });
            updatedCounts.users = usersResult.data.length;
          } catch (error) {
            console.error("Error fetching users data:", error);
          }
        }
        
        return updatedCounts;
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        throw error; // Let React Query handle the error
      }
    },
    {
      enabled: !!teamId,
      staleTime: 0, // Always refetch on mount
      onSuccess: (data) => {
        setCounts(data);
      }
    }
  );

  // Fetch limits and voipnumber from settings
  const { isLoading: settingsLoading, refetch: refetchSettings } = useQuery(
    ['dashboardSettings', teamId],
    async () => {
      try {
        // Fetch settings
        const settingsResult = await dataProvider.getList('settings', {
          pagination: { page: 1, perPage: 100 }
        });

        const settings = settingsResult.data || [];

        // Find max_contacts, max_calendars, and voipnumber settings
        const maxContacts = settings.find(s => s.key === 'max_contacts')?.value;
        const maxCalendars = settings.find(s => s.key === 'max_calendars')?.value;
        const voipNumberValue = settings.find(s => s.key === 'voipnumber')?.value;

        return {
          max_contacts: maxContacts ? parseInt(maxContacts, 10) : null,
          max_calendars: maxCalendars ? parseInt(maxCalendars, 10) : null,
          voipnumber: voipNumberValue || null
        };
      } catch (error) {
        console.error("Error loading settings data:", error);
        throw error;
      }
    },
    {
      enabled: !!teamId,
      staleTime: 60000, // 1 minute - settings don't change as often
      onSuccess: (data) => {
        setLimits({
          max_contacts: data.max_contacts,
          max_calendars: data.max_calendars
        });
        setVoipNumber(data.voipnumber);
      }
    }
  );

  // Force dashboard counts to refresh on component mount
  useEffect(() => {
    const refreshData = async () => {
      if (teamId) {
        await refetchCounts();
        await refetchSettings();
      }
    };

    refreshData();
  }, [teamId, refetchCounts, refetchSettings]);

  // Handler for the on-duty refresh button
  const handleRefreshOnDuty = async () => {
    setIsRefreshingOnDuty(true);
    try {
      await refetchOnDuty();
    } finally {
      setIsRefreshingOnDuty(false);
    }
  };

  // Calculate usage percentages
  const contactsUsage = limits.max_contacts ? (counts.contacts / limits.max_contacts) * 100 : 0;
  const calendarsUsage = limits.max_calendars ? (counts.calendars / limits.max_calendars) * 100 : 0;

  // Determine if we're still loading
  const isLoading = countsLoading || settingsLoading;
  const onDutyErrorMessage = 
    (onDutyError?.body?.error) || // Access error property in the response body
    onDutyError?.message || // Fallback to message if available
    (onDutyError ? String(onDutyError) : null); // Final fallback to stringified error

  return (
    <Card>
      <Title title="Dashboard" />
      <CardContent>
        {/* On-duty contact box with dedicated refresh button */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactEmergencyIcon sx={{ mr: 1, color: 'error.main' }} /> 
              Currently On Duty{voipNumber ? ` for ${formatPhoneNumber(voipNumber)}` : ''}
            </Typography>
            <Tooltip title="Refresh on-duty information">
              <IconButton 
                onClick={handleRefreshOnDuty} 
                disabled={isRefreshingOnDuty || onDutyLoading || isOnDutyFetching}
                color="primary"
                aria-label="refresh on-duty contact"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <Paper elevation={3} sx={{ p: 3 }}>
            {onDutyLoading || isRefreshingOnDuty || isOnDutyFetching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : onDutyError ? (
              <Box sx={{ p: 2, color: 'text.secondary' }}>
                <Typography variant="body1" color="error" sx={{ mt: 1 }}>
                  {onDutyErrorMessage}
                </Typography>
              </Box>
            ) : !onDutyContact || Object.keys(onDutyContact).length === 0 ? (
              <Box sx={{ p: 2, color: 'text.secondary' }}>
                <Typography variant="body1">
                  No one is currently on duty.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Check your calendar configuration to ensure duty schedules are set up correctly.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Contact Name:</Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {onDutyContact.fullname}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle1">Phone:</Typography>
                    <Typography 
                      variant="h6"
                      sx={{ 
                        wordBreak: 'break-all',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {onDutyContact.phone}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Keyword:</Typography>
                    <Typography 
                      variant="h6"
                      sx={{ 
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {onDutyContact.keyword}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle1">Calendar:</Typography>
                    <Typography 
                      variant="h6"
                      sx={{ 
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {onDutyContact.calendar || 'Not specified'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Box>

        {/* Calendar Warning Widget */}
        <CalendarWarningWidget />

        {/* Recent Call History Section */}
        <RecentCalls />
        
        {/* Resource stats grid section */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={role === 'tenant_admin' ? 6 : 6}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <ContactPhoneIcon sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
                <Typography variant="h4">
                  {counts.contacts}
                  {limits.max_contacts && (
                    <Typography component="span" variant="h6" color="text.secondary">
                      {` / ${limits.max_contacts}`}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="h6">Contacts</Typography>
                {limits.max_contacts && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(contactsUsage, 100)} 
                      color="primary"
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: 'secondary.main', // Consistent color regardless of percentage
                          opacity: 0.8
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(contactsUsage)}% used
                    </Typography>
                  </Box>
                )}
                <Button 
                  href="#/contacts" 
                  variant="outlined" 
                  size="small" 
                  sx={{ mt: 1 }}
                >
                  Manage
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={role === 'tenant_admin' ? 6 : 6}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <EventIcon sx={{ fontSize: 40, mb: 1, color: 'secondary.main' }} />
                <Typography variant="h4">
                  {counts.calendars}
                  {limits.max_calendars && (
                    <Typography component="span" variant="h6" color="text.secondary">
                      {` / ${limits.max_calendars}`}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="h6">Calendars</Typography>
                {limits.max_calendars && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(calendarsUsage, 100)} 
                      color="secondary"
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: 'secondary.main', // Consistent color regardless of percentage
                          opacity: 0.8
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(calendarsUsage)}% used
                    </Typography>
                  </Box>
                )}
                {/* Calendar Status List */}
                <Box mt={2} mb={2} sx={{ textAlign: 'left' }}>
                  <CalendarStats />
                </Box>
                <Button 
                  href="#/calendars" 
                  variant="outlined" 
                  size="small" 
                  sx={{ mt: 1 }}
                >
                  Manage
                </Button>
              </Paper>
            </Grid>
            
            {/* Admin-only sections */}
            {role === 'tenant_admin' && (
              <>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper elevation={3} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                    <GroupWorkIcon sx={{ fontSize: 40, mb: 1, color: 'success.main' }} />
                    <Typography variant="h4">{counts.teams}</Typography>
                    <Typography variant="h6">Teams</Typography>
                    <Button
                      href="#/teams"
                      variant="contained"
                      color="success"
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Manage Teams
                    </Button>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper elevation={3} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                    <PeopleIcon sx={{ fontSize: 40, mb: 1, color: 'info.main' }} />
                    <Typography variant="h4">{counts.users}</Typography>
                    <Typography variant="h6">Users</Typography>
                    <Button
                      href="#/users"
                      variant="contained"
                      color="info"
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Manage Users
                    </Button>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};